import { Grid, Stack, Typography } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import ClearIcon from "@mui/icons-material/Clear";
import { black, blue } from "@/theme/colors";
import { useFormContext, useWatch } from "react-hook-form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEffect, useMemo, useState } from "react";
import { useEnum } from "@/api/services/global/enum";
import { Select } from "@/components/elements/Select";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";

export const TalentFormEducationFields = () => {
  const { control, setValue } = useFormContext<TTalentForm>();
  const { data: educationLevelOptions } = useEnum("EEducationLevel");
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const educationBackground = useWatch({
    control,
    name: "educationBackground",
  }) || [null];

  const hasEmptyField = useMemo(() => educationBackground.some((value) => !value || value === null || value === undefined), [educationBackground]);

  const MAX_OPTION = educationLevelOptions?.length;

  const handleAddField = () => {
    if (educationBackground.length < MAX_OPTION && !hasEmptyField) {
      setValue("educationBackground", [...educationBackground, null]);
    }
  };

  const handleRemoveField = (index: number) => {
    if (educationBackground.length > 1) {
      const value = educationBackground[index];
      if (value === null || value === undefined) {
        const updated = educationBackground.filter((_, i) => i !== index);
        setValue("educationBackground", updated);
      } else {
        setDeleteIndex(index);
        setIsDeleteModalOpen(true);
      }
    }
  };

  const confirmDelete = () => {
    if (deleteIndex !== null && educationBackground.length > 1) {
      const updated = educationBackground.filter((_, i) => i !== deleteIndex);
      setValue("educationBackground", updated);
    }
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  useEffect(() => {
    if (!educationBackground || educationBackground.length === 0) {
      setValue("educationBackground", [null]);
    }
  }, [educationBackground, setValue]);

  const getFilteredOptions = (index: number, currentValue: string | number | null | undefined) => {
    if (!educationLevelOptions) return [];

    const selectedValues = educationBackground
      .map((value, i) => (i !== index && value != null ? value.toString() : null))
      .filter((value): value is string => value !== null);

    return educationLevelOptions.filter((option) => {
      const optionValue = option.value.toString();
      if (currentValue != null && optionValue === currentValue.toString()) {
        return true;
      }
      return !selectedValues.includes(optionValue);
    });
  };

  return (
    <>
      <Stack mt={3}>
        <Typography variant="cap12" sx={{ mb: 1 }}>
          学歴
        </Typography>

        {educationBackground.map((value, index) => {
          const filteredOptions = getFilteredOptions(index, value);

          return (
            <Grid container spacing={3} key={index} sx={{ mb: educationBackground.length - 1 === index ? 0 : 2 }}>
              <Grid item xs={12}>
                <FormField control={control} name={`educationBackground.${index}`}>
                  <Select options={filteredOptions} />
                </FormField>
              </Grid>
              {educationBackground.length > 1 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ cursor: "pointer", mt: 1, ml: 3 }}
                  onClick={() => handleRemoveField(index)}
                >
                  <ClearIcon sx={{ color: black[80], fontSize: 18 }} />
                  <Typography variant="cap12Light">削除</Typography>
                </Stack>
              )}
            </Grid>
          );
        })}

        {educationBackground.length < MAX_OPTION && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              cursor: hasEmptyField ? "not-allowed" : "pointer",
              opacity: hasEmptyField ? 0.5 : 1,
            }}
            onClick={handleAddField}
            mt={2}
          >
            <AddCircleOutlineIcon
              sx={{
                color: hasEmptyField ? black[60] : blue[80],
                fontSize: 18,
              }}
            />
            <Typography variant="cap12">学歴を追加</Typography>
          </Stack>
        )}
      </Stack>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onOk={confirmDelete}
        message="「学歴」を削除してもよろしいですか？"
      />
    </>
  );
};
